import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Button, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ptBR } from '@mui/x-data-grid/locales';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from "services/api";
import { cpfMask } from "utils/cpf";
import { useParams } from "react-router-dom";

interface IResponse {
  TAX_PAYER_ID: string;
  NAME: string;
  IS_COMPLETED: boolean;
  PERSON_TYPE: 'employee' | 'dependent';
  GROUP_ID: string;
  CORRECTED_FIELDS: string;
  EMPLOYEE_EMAIL: string;
  EVENT: "open" | "processed" | "delivered" | "queued" | "click" | "done" | "dropped" | "pending";
}

interface IStatusResponse {
  event: "open" | "processed" | "delivered" | "queued" | "click" | "done" | "dropped";
  event_ts: string;
}

interface IFilters {
  personName: string,
  personType: 'all' | 'employee' | 'dependent',
}

function formatDate(dateToFormat: string) {
  try {
    if (!dayjs(dateToFormat).isValid()) throw new Error();
    
    return dayjs(dateToFormat).format(`DD/MM/YYYY - HH:mm:ss`);
  } catch (error) {
    return `--/--/---- --:--:--`
  }
}

/**
 * Seller deve:
 * - Buscar endpoint que traz TODAS as pessoas que tem dados faltantes (fará pooling para atualizar coluna STATUS)
 * - Clicar botão de baixar que fará um POST e enviará o email pro user
 * 
 * Atenção:
 * - Diferente da view do Buyer, Seller NAO poderá selecionar pessoas no checkbox
 * - Diferente da view do Buyer, Seller TERÁ a coluna STATUS
 */
export function MissingDataSeller({ buyerOrganizationId }: { buyerOrganizationId: string }) {
  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string; };
  // const workspaceId = "2fd56b44-899e-4b31-ae80-d7a0c0226262";
  // const workspaceId = "19bf4167-6462-4cd6-ba7f-a04efc8a8a97";
  // const workspaceIdEmail = "2fd56b44-899e-4b31-ae80-d7a0c0226262" //Separei pq galcor da bugado. esse pega email da galcor, de cima o user da cb

  const [currentFilters, setCurrentFilters] = useState<IFilters>({
    personName: '',
    personType: 'all',
  });

  const [emailStatus, setEmailStatus] = useState<Record<string, string>>({});

  const handleFilters = (filters: IFilters) => {
    setCurrentFilters({ ...filters });
  };

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["missing-data", 12],
    queryFn: () => api.get<IResponse[]>(`/missing-data/status-missing-data/${buyerOrganizationId}/${workspaceId}`),
    select: (data) => data.data,
    refetchInterval: 5000
  });

  const employeeStatus = useMutation({
    mutationFn: async (email: string) => {
      console.log("TESTE", email)
      const { data } = await api.post<IStatusResponse>(`/missing-data/sendgrid-missing-data/status`, {
        email: email,
        organizationId: buyerOrganizationId,
        workspaceId: workspaceId,
      });

      console.log(data)
      setEmailStatus(prev => ({...prev, [email]: data?.event ?? "erro" }))

      return data;
    },
    onSuccess: (data) => {
      console.log("[Success @ EMPLOYEE_STATUS]", data);

      return data
    },
    onError: (err) => {
      console.error("[Error @ EMPLOYEE_STATUS]", err);
    },
  });

  console.log("!!!!!!>>>>>>>>>>>>>>>>>>>>>>", data?.length)

  const panels = !data ? {} : {
    "Total": data.length,
    "Titulares": data.filter(item => item.PERSON_TYPE === 'employee').length,
    "Dependentes": data.filter(item => item.PERSON_TYPE === 'dependent').length,
  };

  // Configuração de status centralizada
  const statusConfig: Record<string, { label: string; color: string; backgroundColor: string }> = {
    open: { label: 'Email aberto', color: '#2E7D32', backgroundColor: '#2E7D3214' },
    processed: { label: 'Processado', color: '#EF6C00', backgroundColor: '#EF6C0014' },
    delivered: { label: 'Caixa de entrada', color: '#2e437d', backgroundColor: '#2e337d13' },
    queued: { label: 'Enviado', color: '#232323', backgroundColor: '#9d9d9d13' },
    click: { label: 'Email clicado', color: '#2E7D32', backgroundColor: '#2E7D3214' },
    done: { label: 'Formulário preenchido', color: '#2E7D32', backgroundColor: '#2E7D3214' },
    dropped: { label: 'Erro', color: '#ff7272', backgroundColor: '#EF6C0014' },
    pending: { label: 'Erro', color: '#ff7272', backgroundColor: '#EF6C0014' },
    default: { label: 'Pendente', color: '#232323', backgroundColor: '#9d9d9d13' }
  };

  // Função para obter o status final
  const getFinalStatus = (event: string, emailStatus: Record<string, string>, employeeEmail: string) => {
    return Object.entries(emailStatus).find(([key, value]) => key === employeeEmail)?.[1] || event;
  };

  // Função para renderizar chips de status
  const renderStatusChip = (event: string, emailStatus: Record<string, string>, employeeEmail: string) => {
    const status = getFinalStatus(event, emailStatus, employeeEmail);
    const config = statusConfig[status] || statusConfig.default;
    
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "start" }}>
        <div style={{ 
          color: config.color, 
          backgroundColor: config.backgroundColor, 
          opacity: '0.8', 
          padding: '0.25rem 0.75rem', 
          borderRadius: '0.75rem', 
          fontWeight: '400', 
          fontSize: '12px', 
          lineHeight: '16px', 
          width: 'fit-content', 
          whiteSpace: 'nowrap', 
          textOverflow: 'ellipsis' 
        }}>
          <span>{config.label}</span>
        </div>
      </div>
    );
  };

  // Definição das colunas do DataGrid
  const columns: GridColDef[] = [
    { 
      field: 'name', 
      headerName: 'Nome', 
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => params.value || '-'
    },
    { 
      field: 'taxPayerId', 
      headerName: 'CPF/CNPJ', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => cpfMask(params.value as string)
    },
    { 
      field: 'personType', 
      headerName: 'Parentesco', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => 
        params.value === 'employee' ? 'Titular' : 'Dependente'
    },
    { 
      field: 'status', 
      headerName: 'Status formulário', 
      flex: 0.8,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        return renderStatusChip(row.event, emailStatus, row.employeeEmail);
      },
      sortComparator: (v1, v2, param1, param2) => {
        const status1 = getFinalStatus(param1.api.getRow(param1.id)?.event, emailStatus, param1.api.getRow(param1.id)?.employeeEmail);
        const status2 = getFinalStatus(param2.api.getRow(param2.id)?.event, emailStatus, param2.api.getRow(param2.id)?.employeeEmail);
        
        const label1 = (statusConfig[status1] || statusConfig.default).label;
        const label2 = (statusConfig[status2] || statusConfig.default).label;
        
        return label1.localeCompare(label2, 'pt-BR');
      }
    },
    { 
      field: 'actions', 
      headerName: 'Atualizar status', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton 
          onClick={() => employeeStatus.mutate(params.row.employeeEmail)} 
          aria-label="refresh"
        >
          <RefreshIcon />
        </IconButton>
      ),
      sortable: false,
      filterable: false,
      disableColumnMenu: true
    }
  ];

  // Filtragem de dados
  const filteredData = data ? data.filter(item => 
    (item?.NAME?.toLowerCase().includes(currentFilters.personName.toLowerCase()) || 
     item?.TAX_PAYER_ID?.toLowerCase().includes(currentFilters.personName.toLowerCase())) && 
    (currentFilters.personType === 'all' || currentFilters.personType === item.PERSON_TYPE)
  ) : [];

  // Transformação dos dados para o DataGrid
  const rows = filteredData.map((person, index) => ({
    id: `${person.TAX_PAYER_ID}_${index}`,
    name: person.NAME,
    taxPayerId: person.TAX_PAYER_ID,
    personType: person.PERSON_TYPE,
    event: person.EVENT,
    employeeEmail: person.EMPLOYEE_EMAIL
  }));

  return (
    <>
      {!isFetched && isFetching && <div>Loading...</div>}
      {!isFetched && isError && <div>Error...</div>}
      {data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600,marginBottom: '24px'}}>Gestão de dados faltantes</h1>
  
            {/** Botoes a direita (grid, table, etc) */}
            <div style={{"display":"flex","gap":"0.25rem", alignItems: "center"}}>
              <Button
                variant="outlined"
                style={{
                  paddingTop: "0.25rem",
                  paddingBottom: "0.25rem",
                  paddingLeft: "1rem",
                  paddingRight: "1rem",
                  borderRadius: "8px",
                  height: "30px",
                  fontSize: "0.875rem",
                  lineHeight: "1.25rem",
                  fontWeight: 600,
                }}
                // onClick={() => setIsModalStatusOpen(true)}
              >
                Atividades relacionadas
              </Button>
            </div>
          </div>

          {/** Interface de Data */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: '50px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Data do último envio</p>
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '600', color: '#6F6F78' }}>{formatDate("24/06/2002")}</span>
            </div>
          </div>

          {/** Métricas (Total, Inclusoes, etc) */}
          <div style={{"display":"flex","gap":"1rem"}}>
            {Object.entries(panels).map(([title, value]) => (
              <>
                {title === 'Total' && (
                  <div key={title} style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', marginBottom: '30px' }}>
                    <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>{title}</p>
                    <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.length}</span>
                  </div>
                )}
                {title === 'Titulares' && (
                  <div key={title} style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', marginBottom: '30px' }}>
                    <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>{title}</p>
                    <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.filter(d => d.PERSON_TYPE === "employee").length}</span>
                  </div>
                )}
                {title === 'Dependentes' && (
                  <div key={title} style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', marginBottom: '30px' }}>
                    <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>{title}</p>
                    <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.filter(d => d.PERSON_TYPE === "dependent").length}</span>
                  </div>
                )}
              </>
            ))}
          </div>

          {/** DATAGRID */}
          <div style={{ width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              style={{ backgroundColor: "white" }}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              sx={{
                "& .MuiDataGrid-root": {
                  border: 'none',
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: 'none',
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: '#f5f5f5',
                  borderBottom: 'none',
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: '#ffffff',
                },
              }}
            />
          </div>

          {/** BOTOES */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end',"paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              // onClick={() => setIsModalExportOpen(true)}
            >
              Exportar arquivo
            </Button>
          </div>
        </>
      )}
    </>
  );
}
